import { Input } from '@/components/ui';
import { CANADIAN_PROVINCES, COUNTRIES } from '../types';

interface RequiredFields {
  streetAddress?: boolean;
  city?: boolean;
  province?: boolean;
  postalCode?: boolean;
  country?: boolean;
}

// Default: all fields required (backward compatible)
const DEFAULT_REQUIRED_FIELDS: RequiredFields = {
  streetAddress: true,
  city: true,
  province: true,
  postalCode: true,
  country: true,
};

interface AddressFieldsProps {
  streetAddress: string;
  unit: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  onChange: (field: string, value: string) => void;
  errors?: {
    streetAddress?: string;
    unit?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  };
  disabled?: boolean;
  fieldPrefix?: string; // Optional prefix for field names (e.g., "billing" -> "billingStreetAddress")
  requiredFields?: RequiredFields; // Configure which fields are required (default: all)
}

export function AddressFields({
  streetAddress,
  unit,
  city,
  province,
  postalCode,
  country,
  onChange,
  errors = {},
  disabled = false,
  fieldPrefix = 'billing', // Default prefix
  requiredFields = DEFAULT_REQUIRED_FIELDS,
}: AddressFieldsProps) {
  // Merge with defaults to ensure all fields have a value
  const required = { ...DEFAULT_REQUIRED_FIELDS, ...requiredFields };
  // Helper to build field name with prefix
  const getFieldName = (field: string): string => {
    if (!fieldPrefix) return field;
    // Capitalize first letter of field name when adding prefix
    const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
    return `${fieldPrefix}${capitalizedField}`;
  };

  return (
    <div className="space-y-4">
      {/* Street Address */}
      <Input
        label={required.streetAddress ? 'Street Address' : 'Street Address (Optional)'}
        name="streetAddress"
        value={streetAddress}
        onChange={(e) => onChange(getFieldName('streetAddress'), e.target.value)}
        placeholder="123 Main Street"
        error={errors.streetAddress}
        disabled={disabled}
        required={required.streetAddress}
      />

      {/* Unit/Suite (Optional) */}
      <Input
        label="Unit / Suite (Optional)"
        name="unit"
        value={unit}
        onChange={(e) => onChange(getFieldName('unit'), e.target.value)}
        placeholder="Apt 4B"
        error={errors.unit}
        disabled={disabled}
      />

      {/* City and Province Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label={required.city ? 'City' : 'City (Optional)'}
          name="city"
          value={city}
          onChange={(e) => onChange(getFieldName('city'), e.target.value)}
          placeholder="Toronto"
          error={errors.city}
          disabled={disabled}
          required={required.city}
        />

        {/* Province Dropdown */}
        <div className="w-full">
          <label htmlFor="province" className="input-label">
            Province{required.province ? ' ' : ' (Optional) '}
            {required.province && <span className="text-red-500">*</span>}
          </label>
          <select
            id="province"
            name="province"
            value={province}
            onChange={(e) => onChange(getFieldName('province'), e.target.value)}
            className={`input-field ${errors.province ? 'input-error' : ''}`}
            disabled={disabled}
            required={required.province}
          >
            <option value="">Select Province</option>
            {CANADIAN_PROVINCES.map((prov) => (
              <option key={prov.value} value={prov.value}>
                {prov.label}
              </option>
            ))}
          </select>
          {errors.province && (
            <p className="error-message">{errors.province}</p>
          )}
        </div>
      </div>

      {/* Postal Code and Country Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label={required.postalCode ? 'Postal Code' : 'Postal Code (Optional)'}
          name="postalCode"
          value={postalCode}
          onChange={(e) => onChange(getFieldName('postalCode'), e.target.value)}
          placeholder="M5V 1A1"
          error={errors.postalCode}
          disabled={disabled}
          required={required.postalCode}
        />

        {/* Country Dropdown */}
        <div className="w-full">
          <label htmlFor="country" className="input-label">
            Country{required.country ? ' ' : ' (Optional) '}
            {required.country && <span className="text-red-500">*</span>}
          </label>
          <select
            id="country"
            name="country"
            value={country}
            onChange={(e) => onChange(getFieldName('country'), e.target.value)}
            className={`input-field ${errors.country ? 'input-error' : ''}`}
            disabled={disabled}
            required={required.country}
          >
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          {errors.country && <p className="error-message">{errors.country}</p>}
        </div>
      </div>
    </div>
  );
}